import merge from 'lodash.merge';
import { useRef, useState } from 'react';
import { IFileProgress } from '../../components/FileUpload/FileProgress/FileProgress';
import { IFileUploadProps } from '../../components/FileUpload/FileUpload';

export type UseFileUploadResult = {
  fileProgress: IFileProgress[];
  createFileProgress: (files: File[]) => CreateFileProgressResult;
  updateFileProgress: (fileName: string, progress: number) => boolean;
  removeFileProgress: (fileName: string) => boolean;
  fileUploadProps: IFileUploadProps;
};

export enum UseFileUploadError {
  NonUniqueFileNames
}

export type CreateFileProgressResult = { created?: File[]; duplicates?: File[]; error?: UseFileUploadError };

const defaultProps: IFileUploadProps = {
  maxSize: 5_000_000,
  multiple: true,
  translations: {
    cta: 'Click to upload',
    deleted: 'deleted',
    failed: 'Failed',
    filesLabel: 'Files',
    instruction: 'or drag and drop file(s)',
    max: 'Max.',
    or: 'or',
    uploaded: 'Uploaded',
    uploading: 'Uploading'
  },
  disabled: false,
  required: false
};

export const useFileUpload = (options?: IFileUploadProps): UseFileUploadResult => {
  const [progressState, setProgressState] = useState<IFileProgress[]>([]);
  const progressStateRef = useRef(progressState);

  const createFileProgress = (files: File[]): CreateFileProgressResult => {
    // check if multiple files with the same name have been passed in
    if (new Set(files.map(({ name }) => name)).size !== files.length) {
      return { error: UseFileUploadError.NonUniqueFileNames };
    }

    const currentProgress = progressStateRef.current;
    const duplicates = files.filter(({ name }) => currentProgress.some(({ file: existingFile }) => existingFile.name === name));
    const newFiles = files.filter(({ name }) => !currentProgress.some(({ file: existingFile }) => existingFile.name === name));
    if (newFiles.length > 0) {
      const updatedProgress = [
        ...currentProgress,
        ...newFiles.map((file) => ({
          file,
          progress: 0
        }))
      ];
      setProgressState(updatedProgress);
      progressStateRef.current = updatedProgress;
    }
    return { created: newFiles, duplicates: duplicates };
  };

  const removeFileProgress = (fileName: string): boolean => {
    const currentProgress = progressStateRef.current;
    if (!currentProgress.some(({ file }) => file.name === fileName)) return false;
    const updatedProgress = currentProgress.filter(({ file }) => file.name !== fileName);
    setProgressState(updatedProgress);
    progressStateRef.current = updatedProgress;
    return true;
  };

  const updateFileProgress = (fileName: string, progressValue: number): boolean => {
    const currentProgress = progressStateRef.current;
    if (!currentProgress.some(({ file }) => file.name === fileName)) return false;

    const updatedProgress = currentProgress.map((currentProgressItem) => {
      if (currentProgressItem.file.name === fileName) {
        currentProgressItem.progress = Math.min(Math.max(0, progressValue), 1);
      }

      return currentProgressItem;
    });

    setProgressState(updatedProgress);
    progressStateRef.current = updatedProgress;
    return true;
  };

  return {
    fileProgress: progressState,
    createFileProgress,
    updateFileProgress,
    removeFileProgress,
    fileUploadProps: merge(defaultProps, options)
  };
};
